import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Row from '../components/Row';
import HeroCarousel from '../components/HeroCarousel';
import ModelRowStatus from '../components/ModelRowStatus';
import { Persona } from '../data/personas';
import { getRowsForUser, RowItem } from '../recs/engine';
import { useModelRow } from '../recs/useModelRow';
import { posterUrlFor } from '../data/posters';

const HERO_SLIDES = 5;

/** Prefer items with real poster art so the hero doesn't fall back to gradients; dedupe by title. */
function pickHeroItems(rows: { items: RowItem[] }[]): RowItem[] {
  const seen = new Set<string>();
  const withPoster: RowItem[] = [];
  const withoutPoster: RowItem[] = [];
  for (const row of rows) {
    for (const item of row.items) {
      if (seen.has(item.title.title_id)) continue;
      seen.add(item.title.title_id);
      const hasPoster = posterUrlFor(item.title.title_id, item.title.genre_primary) !== null;
      (hasPoster ? withPoster : withoutPoster).push(item);
    }
  }
  return [...withPoster, ...withoutPoster].slice(0, HERO_SLIDES);
}

interface Props {
  persona: Persona;
}

export default function HomePage({ persona }: Props) {
  const navigate = useNavigate();
  const handleSelectItem = (item: RowItem) => navigate(`/title/${item.title.title_id}`);

  const heuristicRows = useMemo(() => getRowsForUser(persona.user), [persona]);
  const { row: modelRow, status: modelRowStatus } = useModelRow(persona.user.user_id);

  const continueWatchingIndex = heuristicRows.findIndex((r) => r.id === 'continue-watching');
  const insertAt = continueWatchingIndex === -1 ? 0 : continueWatchingIndex + 1;
  const rowsBeforeModel = heuristicRows.slice(0, insertAt);
  const rowsAfterModel = heuristicRows.slice(insertAt);

  const isEmpty = heuristicRows.length === 0 && modelRowStatus !== 'loading' && modelRowStatus !== 'error';
  const heroItems = useMemo(() => pickHeroItems(heuristicRows), [heuristicRows]);

  return (
    <>
      {heroItems.length > 0 && (
        <HeroCarousel items={heroItems} viewer={persona.user} onSelect={handleSelectItem} />
      )}
      <main className="App__rows">
        {isEmpty ? (
          <p className="App__empty">No recommendations available for this profile yet.</p>
        ) : (
          <>
            {rowsBeforeModel.map((row) => (
              <Row key={row.id} row={row} viewer={persona.user} onSelectItem={handleSelectItem} />
            ))}
            {modelRow ? (
              <Row row={modelRow} viewer={persona.user} onSelectItem={handleSelectItem} />
            ) : (
              <ModelRowStatus status={modelRowStatus === 'error' ? 'error' : 'loading'} />
            )}
            {rowsAfterModel.map((row) => (
              <Row key={row.id} row={row} viewer={persona.user} onSelectItem={handleSelectItem} />
            ))}
          </>
        )}
      </main>
    </>
  );
}
